/**
 * SelecaoDocumentosPage — fase 2 da auditoria (F10).
 *
 * A leitura já classificou o que veio. Aqui o usuário decide:
 *  - quais documentos entram;
 *  - o PAPEL de cada um — auditar (gera apontamento) ou apenas contexto
 *    (é lido para a análise não julgar peça fora do conjunto);
 *  - as PÁGINAS, quando o arquivo é composto (vários documentos num PDF só).
 *
 * Sem intervalo de páginas num arquivo composto, a IA audita o PDF inteiro
 * como se fosse uma peça só — por isso o campo é exigido ali.
 */

import {
  faCheckCircle,
  faExclamationTriangle,
  faLayerGroup,
  faPlay,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import {
  type DocumentoSelecionado,
  iniciarAuditoria,
  selecaoAuditoria,
} from '../../api/auditoria'
import { useToast } from '../../components/Toast'

type Papel = 'auditar' | 'contexto'
type Escopo = 'focused' | 'global'

interface Escolha {
  marcado: boolean
  papel: Papel
  paginas: string
}

const PADRAO: Escolha = { marcado: false, papel: 'auditar', paginas: '' }

export function SelecaoDocumentosPage() {
  const { id } = useParams()
  const auditId = Number(id)
  const navegar = useNavigate()
  const { notificar } = useToast()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['auditoria', auditId, 'selecao'],
    queryFn: () => selecaoAuditoria(auditId),
    retry: false,
  })

  // chave = tipo do documento (é o que a task consome), com o índice do
  // arquivo composto para não fundir dois PDFs do mesmo tipo
  const [escolhas, setEscolhas] = useState<Record<string, Escolha>>({})
  const [qualitativo, setQualitativo] = useState<Escopo>('focused')
  const [contradicao, setContradicao] = useState<Escopo>('focused')
  const [erro, setErro] = useState('')

  useEffect(() => {
    if (!data) return
    const inicial: Record<string, Escolha> = {}
    // o que já veio classificado entra marcado: é o caminho comum
    data.individuais.forEach((a) => {
      inicial[`ind:${a.indice}:${a.tipo}`] = {
        marcado: true,
        papel: 'auditar',
        paginas: 'todas',
      }
    })
    setEscolhas(inicial)
  }, [data])

  const iniciar = useMutation({
    mutationFn: (documentos: DocumentoSelecionado[]) =>
      iniciarAuditoria(auditId, documentos, qualitativo, contradicao),
    onSuccess: () => {
      notificar('success', 'Auditoria iniciada.')
      navegar(`/tasks/auditoria/${auditId}`)
    },
    onError: (e: Error) => {
      setErro(e.message)
      notificar('error', e.message)
    },
  })

  function atualizar(chave: string, mudanca: Partial<Escolha>) {
    setEscolhas((atual) => ({
      ...atual,
      [chave]: { ...PADRAO, ...atual[chave], ...mudanca },
    }))
  }

  function enviar() {
    const documentos: DocumentoSelecionado[] = []
    for (const [chave, escolha] of Object.entries(escolhas)) {
      if (!escolha.marcado) continue
      const tipo = chave.split(':').slice(2).join(':') || chave
      const composto = chave.startsWith('comp:') || chave.startsWith('man:')
      if (composto && !escolha.paginas.trim()) {
        setErro(
          'Informe as páginas de cada documento selecionado no arquivo composto.',
        )
        return
      }
      documentos.push({
        tipo,
        paginas: composto ? escolha.paginas.trim() : 'todas',
        papel: escolha.papel,
      })
    }
    if (!documentos.length) {
      setErro('Selecione ao menos um documento.')
      return
    }
    if (!documentos.some((d) => d.papel === 'auditar')) {
      setErro(
        'Marque ao menos um documento para auditar — documento de contexto sozinho não gera apontamento.',
      )
      return
    }
    setErro('')
    iniciar.mutate(documentos)
  }

  if (isLoading) {
    return (
      <p className="p-8 text-center text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Carregando documentos…
      </p>
    )
  }

  if (isError || !data) {
    return (
      <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white p-8 text-center dark:border-slate-700 dark:bg-dark-card">
        <h1 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
          Seleção não disponível
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          Esta auditoria não está aguardando seleção de documentos.
        </p>
      </div>
    )
  }

  const linhaPapel = (chave: string, escolha: Escolha) => (
    <div className="mt-2 flex flex-wrap gap-4 text-xs">
      {(
        [
          ['auditar', 'Auditar (gera apontamento)'],
          ['contexto', 'Somente contexto'],
        ] as const
      ).map(([valor, rotulo]) => (
        <label key={valor} className="flex cursor-pointer items-center gap-1.5">
          <input
            type="radio"
            name={`papel-${chave}`}
            checked={(escolha.papel ?? 'auditar') === valor}
            onChange={() => atualizar(chave, { papel: valor })}
          />
          <span className="text-slate-600 dark:text-slate-400">{rotulo}</span>
        </label>
      ))}
    </div>
  )

  return (
    <div className="mx-auto max-w-4xl">
      <header className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <div className="bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500 p-6 text-white">
          <h1 className="flex items-center text-2xl font-bold">
            <FontAwesomeIcon icon={faLayerGroup} className="mr-3" />
            Selecione os Documentos
          </h1>
          <p className="mt-1 text-rose-100">
            {data.arquivo} ·{' '}
            {data.fase === 'julgamento'
              ? 'Fase de Julgamento'
              : 'Fase Preparatória'}
          </p>
        </div>
      </header>

      {erro && (
        <div
          role="alert"
          className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
        >
          <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5" />
          <span>{erro}</span>
        </div>
      )}

      {data.individuais.length > 0 && (
        <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
          <h2 className="mb-1 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
            <FontAwesomeIcon icon={faCheckCircle} className="text-emerald-500" />
            Documentos identificados
          </h2>
          <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
            Arquivos inteiros, já classificados: entram com todas as páginas.
          </p>
          <ul className="space-y-3">
            {data.individuais.map((a) => {
              const chave = `ind:${a.indice}:${a.tipo}`
              const escolha = escolhas[chave] ?? PADRAO
              return (
                <li
                  key={chave}
                  className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-900/20"
                >
                  <label className="flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      checked={escolha.marcado}
                      onChange={(e) =>
                        atualizar(chave, {
                          marcado: e.target.checked,
                          paginas: 'todas',
                        })
                      }
                      className="mt-1"
                    />
                    <span className="min-w-0">
                      <span className="block font-medium text-slate-800 dark:text-slate-100">
                        {a.tipo_rotulo}
                      </span>
                      <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
                        {a.arquivo}
                      </span>
                    </span>
                  </label>
                  {escolha.marcado && linhaPapel(chave, escolha)}
                </li>
              )
            })}
          </ul>
        </section>
      )}

      {(data.compostos.length > 0 || data.selecao_manual) && (
        <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
          <h2 className="mb-1 text-sm font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
            {data.selecao_manual
              ? 'Seleção manual'
              : 'Arquivos com vários documentos'}
          </h2>
          <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
            Marque o que existe no arquivo e informe as páginas de cada peça
            (ex.: <code>1-12</code>). Sem o intervalo, a análise trata o PDF
            inteiro como um documento só.
          </p>

          {(data.selecao_manual
            ? [{ indice: 0, arquivo: data.arquivo }]
            : data.compostos
          ).map((arquivo) => (
            <div key={arquivo.indice} className="mb-4">
              <p className="mb-2 truncate text-sm font-medium text-slate-700 dark:text-slate-200">
                {arquivo.arquivo}
              </p>
              <ul className="space-y-2">
                {data.tipos.map((tipo) => {
                  const prefixo = data.selecao_manual ? 'man' : 'comp'
                  const chave = `${prefixo}:${arquivo.indice}:${tipo.id}`
                  const escolha = escolhas[chave] ?? PADRAO
                  return (
                    <li
                      key={chave}
                      className="rounded-lg border border-slate-200 p-3 dark:border-slate-600"
                    >
                      <div className="flex flex-wrap items-center gap-3">
                        <label className="flex flex-1 cursor-pointer items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={escolha.marcado}
                            onChange={(e) =>
                              atualizar(chave, { marcado: e.target.checked })
                            }
                          />
                          <span className="text-slate-700 dark:text-slate-200">
                            {tipo.rotulo}
                          </span>
                        </label>
                        <input
                          type="text"
                          aria-label={`Páginas de ${tipo.rotulo}`}
                          placeholder="Páginas (ex.: 1-12)"
                          value={escolha.paginas}
                          disabled={!escolha.marcado}
                          onChange={(e) =>
                            atualizar(chave, { paginas: e.target.value })
                          }
                          className="w-40 rounded-lg border border-slate-300 px-3 py-1.5 text-sm disabled:bg-slate-100 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:disabled:bg-slate-800"
                        />
                      </div>
                      {escolha.marcado && linhaPapel(chave, escolha)}
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </section>
      )}

      <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <h2 className="mb-4 text-sm font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
          Profundidade da análise
        </h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {(
            [
              [
                'Análise qualitativa',
                qualitativo,
                setQualitativo,
                'qualitativo',
              ],
              [
                'Detecção de contradições',
                contradicao,
                setContradicao,
                'contradicao',
              ],
            ] as const
          ).map(([rotulo, valor, definir, nome]) => (
            <div key={nome}>
              <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                {rotulo}
              </p>
              {(
                [
                  ['focused', 'Focada (mais rápida)'],
                  ['global', 'Global (documento inteiro)'],
                ] as const
              ).map(([opcao, texto]) => (
                <label
                  key={opcao}
                  className="mb-1 flex cursor-pointer items-center gap-2 text-sm"
                >
                  <input
                    type="radio"
                    name={nome}
                    checked={valor === opcao}
                    onChange={() => definir(opcao)}
                  />
                  <span className="text-slate-600 dark:text-slate-400">
                    {texto}
                  </span>
                </label>
              ))}
            </div>
          ))}
        </div>
      </section>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={() => navegar('/auditoria')}
          className="rounded-xl border border-slate-300 px-6 py-3 font-medium text-slate-600 dark:border-slate-600 dark:text-slate-300"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={enviar}
          disabled={iniciar.isPending}
          className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-rose-500 to-amber-500 px-6 py-3 font-semibold text-white shadow-lg transition-all hover:from-rose-600 hover:to-amber-600 disabled:opacity-60"
        >
          <FontAwesomeIcon
            icon={iniciar.isPending ? faSpinner : faPlay}
            spin={iniciar.isPending}
          />
          {iniciar.isPending ? 'Iniciando…' : 'Iniciar auditoria'}
        </button>
      </div>
    </div>
  )
}
